import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useMediaQuery,
  Drawer,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatIcon from "@mui/icons-material/Chat";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import OtherHousesIcon from "@mui/icons-material/OtherHouses";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import GavelIcon from "@mui/icons-material/Gavel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import {
  faBook,
  faFile,
  faFileContract,
  faFileLines,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faFileSignature } from "@fortawesome/free-solid-svg-icons";

const COLLAPSED_WIDTH = "72px";
const EXPANDED_WIDTH = "256px";

const HomeSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedItem, setSelectedItem] = useState("Home");
  const [isHovered, setIsHovered] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  const isExpanded = isOpen || isHovered;

  const handleItemClick = (item) => {
    if (onClose) onClose();
    setIsHovered(false);
    setSelectedItem(item);

    switch (item) {
      case "Home":
        navigate("/app/home");
        break;
      case "Legal Chat":
        navigate("/app/legal-chat");
        break;
      case "Case Search":
        navigate("/app/searchResults");
        break;
      case "Statute Search":
        navigate("/app/statute-search");
        break;
      case "Petition Drafting":
        navigate("/app/petition-drafting");
        break;
      case "Application Drafting":
        navigate("/app/application-drafting");
        break;
      case "Contract Drafting":
        navigate("/app/contract-drafting");
        break;
      case "Bookmarks":
        navigate("/app/bookmarks");
        break;
      case "Legal Library":
        navigate("/app/legal-library");
        break;
      case "Case Management":
        navigate("/app/case-management");
        break;
      case "Document Analysis":
        navigate("/app/document-analysis");
        break;
      case "JudgeGPT":
        navigate("/app/judge-gpt");
        break;
      case "Document Chat":
        navigate("/app/document-chat");
        break;
      case "Contract Analysis":
        navigate("/app/contract-analysis");
        break;
      default:
        navigate("/app/home");
        break;
    }
  };

  useEffect(() => {
    switch (location.pathname) {
      case "/app/home":
        setSelectedItem("Home");
        break;
      case "/app/legal-chat":
        setSelectedItem("Legal Chat");
        break;
      case "/app/searchResults":
        setSelectedItem("Case Search");
        break;
      case "/app/statute-search":
        setSelectedItem("Statute Search");
        break;
      case "/app/petition-drafting":
        setSelectedItem("Petition Drafting");
        break;
      case "/app/application-drafting":
        setSelectedItem("Application Drafting");
        break;
      case "/app/contract-drafting":
        setSelectedItem("Contract Drafting");
        break;
      case "/app/bookmarks":
        setSelectedItem("Bookmarks");
        break;
      case "/app/legal-library":
        setSelectedItem("Legal Library");
        break;
      case "/app/case-management":
        setSelectedItem("Case Management");
        break;
      case "/app/document-analysis":
        setSelectedItem("Document Analysis");
        break;
      case "/app/judge-gpt":
        setSelectedItem("JudgeGPT");
        break;
      case "/app/document-chat":
        setSelectedItem("Document Chat");
        break;
      case "/app/contract-analysis":
        setSelectedItem("Contract Analysis");
        break;
      default:
        break;
    }
  }, [location]);

  const menuItems = [
    { label: "Home", icon: <OtherHousesIcon fontSize="small" /> },
    { label: "Legal Chat", icon: <ChatIcon fontSize="small" /> },
    { label: "Case Search", icon: <GavelIcon fontSize="small" /> },
    { label: "Statute Search", icon: <MenuBookOutlinedIcon fontSize="small" /> },
    { label: "Bookmarks", icon: <BookmarkIcon fontSize="small" /> },
    {
      label: "Application Drafting",
      icon: <FontAwesomeIcon icon={faFileSignature} fontSize="18px" />,
    },
    { label: "Petition Drafting", icon: <AssignmentIcon fontSize="small" /> },
    {
      label: "Contract Drafting",
      icon: <FontAwesomeIcon icon={faFileLines} fontSize="18px" />,
    },
    {
      label: "Legal Library",
      icon: <FontAwesomeIcon icon={faBook} fontSize="18px" />,
    },
    {
      label: "Case Management",
      icon: <FontAwesomeIcon icon={faListCheck} fontSize="18px" />,
    },
    { label: "Document Analysis", icon: <QueryStatsIcon fontSize="small" /> },
    {
      label: "Document Chat",
      icon: <FontAwesomeIcon icon={faFile} fontSize="18px" />,
    },
    {
      label: "Contract Analysis",
      icon: <FontAwesomeIcon icon={faFileContract} fontSize="18px" />,
    },
  ];

  const isActive = (item) => selectedItem === item.label;

  const sidebarContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #E5E7EB",
        width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        transition: "width 0.2s ease-in-out",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 2 }}>
        <List sx={{ px: isExpanded ? 2 : 1 }}>
          {menuItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleItemClick(item.label)}
                selected={isActive(item)}
                sx={{
                  borderRadius: "8px",
                  px: isExpanded ? 2 : 1,
                  py: 1.5,
                  color: isActive(item) ? "#0073A6" : "#374151",
                  backgroundColor: isActive(item) ? "#F0F9FF" : "transparent",
                  justifyContent: isExpanded ? "flex-start" : "center",
                  "&:hover": {
                    backgroundColor: isActive(item) ? "#F0F9FF" : "#F9FAFB",
                    color: isActive(item) ? "#0073A6" : "#111827",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#F0F9FF",
                    "&:hover": { backgroundColor: "#F0F9FF" },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isExpanded ? "40px" : "auto",
                    color: isActive(item) ? "#0073A6" : "#6B7280",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isExpanded && (
                  <ListItemText
                    primary={item.label}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "14px",
                        fontWeight: isActive(item) ? 500 : 400,
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {isExpanded && (
        <Box sx={{ px: 3, py: 3, borderTop: "1px solid #F3F4F6" }}>
          <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "12px" }}>
            © 2026 LawGPT
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        id="home-sidebar-mobile"
        anchor="left"
        open={isOpen}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: EXPANDED_WIDTH,
            boxSizing: "border-box",
            border: "none",
            boxShadow: "4px 0 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      id="home-sidebar-main"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "fixed",
        top: "64px",
        left: 0,
        height: "calc(100vh - 64px)",
        zIndex: 1200,
        transition: "width 0.2s ease-in-out",
        boxShadow: isHovered && !isOpen ? "4px 0 16px rgba(0,0,0,0.10)" : "none",
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default HomeSidebar;
